import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { helpdeskAPI } from '../services/api';
import './Dashboard.css';

function Dashboard() {
    const { client } = useAuth();
    const { t, language } = useLanguage();
    const [stats, setStats] = useState({ 
        total: 0, 
        open_tickets: 0, 
        awaiting_response: 0, 
        resolved_tickets: 0, 
        closed_tickets: 0 
    });
    const [recentTickets, setRecentTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            const [statsRes, ticketsRes] = await Promise.all([
                helpdeskAPI.getStats(),
                helpdeskAPI.getTickets({ limit: 5 })
            ]);
            
            if (statsRes.data.success) {
                setStats(statsRes.data.stats);
            }
            if (ticketsRes.data.success) {
                setRecentTickets(ticketsRes.data.tickets);
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'open': { label: t('statusOpen'), class: 'open' },
            'assigned': { label: t('statusAssigned'), class: 'assigned' },
            'in_progress': { label: t('statusInProgress'), class: 'in-progress' },
            'waiting_for_customer': { label: t('statusWaiting'), class: 'awaiting' },
            'resolved': { label: t('statusResolved'), class: 'resolved' },
            'closed': { label: t('statusClosed'), class: 'closed' }
        };
        const s = statusMap[status] || { label: status, class: 'default' };
        return <span className={`status-badge ${s.class}`}>{s.label}</span>;
    };

    if (loading) {
        return <div className="loading">{t('loading')}</div>;
    }

    return (
        <div className="dashboard-page">
            <div className="welcome-section">
                <div>
                    <h1>{t('welcomeBack')}، {client?.name || 'User'}!</h1>
                    <p>{t('ticketsOverview')}</p>
                </div>
                <Link to="/tickets/new" className="new-ticket-btn">
                    + {t('newTicket')}
                </Link>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-card-icon blue">📋</div>
                    <div className="stat-card-value">{stats.total || 0}</div>
                    <div className="stat-card-label">{t('totalTickets')}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-icon green">🔓</div>
                    <div className="stat-card-value">{stats.open_tickets || 0}</div>
                    <div className="stat-card-label">{t('openTickets')}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-icon orange">⏳</div>
                    <div className="stat-card-value">{stats.awaiting_response || 0}</div>
                    <div className="stat-card-label">{t('awaitingResponse')}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-icon teal">✅</div>
                    <div className="stat-card-value">{stats.resolved_tickets || 0}</div>
                    <div className="stat-card-label">{t('resolved')}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-icon gray">🔒</div>
                    <div className="stat-card-value">{stats.closed_tickets || 0}</div>
                    <div className="stat-card-label">{t('closed')}</div>
                </div>
            </div>

            <div className="recent-section">
                <div className="section-header">
                    <h2>{t('recentTickets')}</h2>
                    <Link to="/tickets" className="view-all-link">{t('viewAll')} ←</Link>
                </div>

                {recentTickets.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">📭</div>
                        <h3>{t('noTicketsYet')}</h3>
                        <p>{t('createFirstTicket')}</p>
                        <Link to="/tickets/new" className="create-ticket-btn">{t('createTicket')}</Link>
                    </div>
                ) : (
                    <div className="tickets-list">
                        <table className="tickets-table">
                            <thead>
                                <tr>
                                    <th>{t('ticketNumber')}</th>
                                    <th>{t('subject')}</th>
                                    <th>{t('status')}</th>
                                    <th>{t('created')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentTickets.map(ticket => (
                                    <tr key={ticket.id}>
                                        <td>
                                            <Link to={`/tickets/${ticket.id}`} className="ticket-number">
                                                #{ticket.ticket_number}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link to={`/tickets/${ticket.id}`} className="ticket-subject">
                                                {ticket.subject}
                                            </Link>
                                        </td>
                                        <td>{getStatusBadge(ticket.status)}</td>
                                        <td className="ticket-date">{formatDate(ticket.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="help-section">
                <h2>{t('quickHelp')}</h2>
                <div className="help-grid">
                    <div className="help-card">
                        <div className="help-card-icon">📚</div>
                        <h3>{t('knowledgeBase')}</h3>
                        <p>{t('browseDocumentation')}</p>
                    </div>
                    <div className="help-card">
                        <div className="help-card-icon">💬</div>
                        <h3>{t('whatsappSupport')}</h3>
                        <p>{t('chatOnWhatsapp')}</p>
                    </div>
                    <div className="help-card">
                        <div className="help-card-icon">📞</div>
                        <h3>{t('contactUs')}</h3>
                        <p>{t('reachUs')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
